import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Button } from '@indigo-ag/component-module';
import { NotificationSection } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/NotificationSection/NotificationSection';

import styled from 'styled-components';
import ConditionalsEdit from './ConditionalsEdit';
import ConditionalsTable from './ConditionalsTable';
import i18next from 'i18next';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { createFromCreditApprovalData, createFromImportedConditionals } from './ConditionalsEdit.utils';
import { UserContext } from 'core/components/GateWay/GateWay';
import { Phase3 } from '@indigo-ag/schema';
import { useApproveConditionals } from './hooks/useApproveConditionals';
import { ConfirmationDialog as ConfirmationModal } from 'shared/components/ConfirmationDialog/ConfirmationDialog';

interface ConditionalValidationsProps {
  phase3Info: Phase3;
}

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
`;

const ConditionalsValidation = ({ phase3Info }: ConditionalValidationsProps) => {
  const { creditApprovalDataQuery, importedConditionals, agFinancingId } = useContext(FinanceProcessContext);
  const { isCreditLead, role } = useContext(UserContext);

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const isSuperAdmin = role.id === 'financing-admin-super';
  const hasPendency = !phase3Info.approvedConditionals;

  const { approveConditionals, loading } = useApproveConditionals();

  const handleApproveConditionals = useCallback(async () => {
    await approveConditionals({ agFinancingId, onSuccess: () => setOpenConfirmationModal(false) });
  }, [approveConditionals, agFinancingId]);

  const conditionals = useMemo(() => {
    if (
      !creditApprovalDataQuery ||
      !creditApprovalDataQuery.data ||
      !creditApprovalDataQuery.data.creditApprovalData.round ||
      creditApprovalDataQuery.data.creditApprovalData.round.creditConditionals.length === 0
    )
      return createFromImportedConditionals(importedConditionals);
    return createFromCreditApprovalData(creditApprovalDataQuery.data.creditApprovalData.round.creditConditionals);
  }, [creditApprovalDataQuery, importedConditionals]);

  const roundId = creditApprovalDataQuery?.data?.creditApprovalData?.round?.id;

  const subtitle = hasPendency
    ? i18next.t('conditionalValidations-subtitle-pendency')
    : i18next.t('conditionalValidations-subtitle');

  const status = hasPendency ? 'warning' : 'success';

  return (
    <>
      <NotificationSection title={i18next.t('conditionalValidations-title')} subtitle={subtitle} status={status}>
        <ConditionalsTable data={conditionals} />
        <ActionsWrapper>
          {hasPendency && roundId && (
            <ConditionalsEdit roundId={roundId} data={conditionals} alreadyAdded={importedConditionals} />
          )}
          {(isCreditLead || isSuperAdmin) && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => setOpenConfirmationModal(true)}
              disabled={!hasPendency && false}
              busy={loading}
            >
              {i18next.t('approveConditionals')}
            </Button>
          )}
        </ActionsWrapper>
      </NotificationSection>
      <ConfirmationModal
        open={openConfirmationModal}
        closeDialog={() => setOpenConfirmationModal(false)}
        onConfirmPromise={handleApproveConditionals}
        confirmationText={i18next.t('conditionalValidations-confirmationText')}
        confirmationButtonText={i18next.t('conditionalValidations-confirmationButtonText')}
        dialogMaxHeight="19rem"
      />
    </>
  );
};

export default ConditionalsValidation;
